import React, { useState, useEffect } from 'react';
import { 
  Container, 
  Typography, 
  Box,
  Paper,
  Alert,
  CircularProgress,
  Button,
  Divider
} from '@mui/material';
import { Navigate, useNavigate } from 'react-router-dom';
import ProductCard from '../components/ProductCard';
import { useAuth } from '../context/AuthContext';
import { productsApi } from '../api/api';
import { Product } from '../types/api';
import FavoriteBorderIcon from '@mui/icons-material/FavoriteBorder';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';

const FavoritesPage: React.FC = () => {
  const { isAuthenticated, loading: authLoading } = useAuth();
  const [favorites, setFavorites] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchFavorites = async () => {
      if (!isAuthenticated) return;
      
      setLoading(true);
      try {
        // Get favorite product IDs
        const favoriteIds = await productsApi.getFavorites();
        
        if (favoriteIds.length === 0) {
          setFavorites([]);
          setLoading(false);
          return;
        }
        
        // Fetch product details for each favorite ID
        const favoriteProducts: Product[] = [];
        for (const id of favoriteIds) {
          try {
            const product = await productsApi.getProduct(id);
            favoriteProducts.push(product);
          } catch (error) {
            console.error(`Error fetching product ${id}:`, error);
            // Continue with other products even if one fails
          }
        }
        
        setFavorites(favoriteProducts);
      } catch (err: any) {
        setError('Failed to load favorites. Please try again later.');
        console.error('Error fetching favorites:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchFavorites();
  }, [isAuthenticated]);

  // Redirect to login if not authenticated
  if (!authLoading && !isAuthenticated) {
    return <Navigate to="/login" state={{ from: '/favorites' }} />;
  }

  const handleBackToShop = () => {
    navigate('/');
  };

  return (
    <Container maxWidth="lg" sx={{ py: 6 }}>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          My Favorites
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Products you've saved as favorites
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
          <CircularProgress />
        </Box>
      ) : favorites.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <FavoriteBorderIcon sx={{ fontSize: 60, color: 'text.secondary', mb: 2 }} />
          <Typography variant="h5" gutterBottom>
            You don't have any favorites yet
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
            Add items to your favorites while shopping to see them here
          </Typography>
          <Button 
            variant="contained"
            color="primary"
            startIcon={<ArrowBackIcon />}
            onClick={handleBackToShop}
          >
            Back to Shop
          </Button>
        </Paper>
      ) : (
        <>
          <Divider sx={{ mb: 4 }} />
          <Box sx={{ 
            display: 'grid',
            gridTemplateColumns: { 
              xs: 'repeat(2, 1fr)',  // 2 items on mobile 
              sm: 'repeat(2, 1fr)',  // 2 items on small tablets
              md: 'repeat(3, 1fr)',  // 3 items on tablets/small laptops
              lg: 'repeat(4, 1fr)',  // 4 items on laptops/desktops
              xl: 'repeat(5, 1fr)'   // 5 items on large screens
            },
            gap: 3
          }}>
            {favorites.map(product => (
              <Box key={product.id} sx={{ width: '100%' }}>
                <ProductCard product={product} />
              </Box>
            ))}
          </Box>
        </>
      )}
    </Container>
  );
};

export default FavoritesPage; 